import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  orderBy,
  limit as fbLimit,
  startAfter,
  where,
} from 'firebase/firestore'
import { firestore, iamAuth } from '@/lib/firebase'
import { AccountSchema, TransactionSchema, type Account, type Transaction, type TransactionStatus } from './types'

function requireUid(): string {
  const uid = iamAuth.currentUser?.uid
  if (!uid) throw new Error('Not signed in')
  return uid
}

export interface TransactionsPage {
  rows: Transaction[]
  /** ISO date of the last row in this page; pass to the next call as
   *  `cursor` to fetch older rows. `null` when there are no more. */
  nextCursor: string | null
}

/**
 * Cursor-paginated read of `users/{uid}/transactions`, ordered by
 * date desc. Cursor is the last row's date string; we pass it via
 * Firestore `startAfter()` so each page picks up where the prior
 * left off without re-reading.
 *
 * `dateFloor` (optional ISO `YYYY-MM-DD`) adds a Firestore
 * `where('date','>=', floor)` so the server doesn't ship rows older
 * than what the UI wants. The composite (date>= + orderBy date desc)
 * uses the auto-built single-field index — no manual index needed.
 *
 * Firestore Web SDK starts to drop requests around the 5-10MB result
 * mark, which is why a flat `limit(20000)` against ~10k YNAB-import
 * rows returned nothing. 2k per page keeps us well under that.
 */
export async function fetchTransactionsPage(
  cursor: string | null,
  pageSize = 2000,
  dateFloor: string | null = null,
): Promise<TransactionsPage> {
  const uid = requireUid()
  const constraints = [
    ...(dateFloor ? [where('date', '>=', dateFloor)] : []),
    orderBy('date', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    fbLimit(pageSize),
  ]
  const q = query(collection(firestore, 'users', uid, 'transactions'), ...constraints)
  const snap = await getDocs(q)
  const rows: Transaction[] = []
  for (const d of snap.docs) {
    const parsed = TransactionSchema.safeParse(d.data())
    if (parsed.success) rows.push(parsed.data)
    else console.warn('transaction doc failed parse, skipping:', d.id, parsed.error)
  }
  const nextCursor = snap.size === pageSize && rows.length > 0
    ? rows[rows.length - 1].date
    : null
  return { rows, nextCursor }
}

/** Convenience wrapper for places that only want the first page. */
export async function fetchTransactions(limitRows = 500): Promise<Transaction[]> {
  const page = await fetchTransactionsPage(null, limitRows)
  return page.rows
}

/**
 * All transactions in [startISO, endISO). Lexicographic string
 * comparison works for both date-only and full ISO-8601 datetimes,
 * so 'YYYY-MM-01' bounds capture a calendar month either way.
 */
export async function fetchTransactionsInRange(
  startISO: string,
  endISO: string,
): Promise<Transaction[]> {
  const uid = requireUid()
  const q = query(
    collection(firestore, 'users', uid, 'transactions'),
    where('date', '>=', startISO),
    where('date', '<', endISO),
    orderBy('date', 'desc'),
  )
  const snap = await getDocs(q)
  const rows: Transaction[] = []
  for (const d of snap.docs) {
    const parsed = TransactionSchema.safeParse(d.data())
    if (parsed.success) rows.push(parsed.data)
  }
  return rows
}

export async function fetchAccounts(): Promise<Account[]> {
  const uid = requireUid()
  const q = query(collection(firestore, 'users', uid, 'accounts'))
  const snap = await getDocs(q)
  const out: Account[] = []
  for (const d of snap.docs) {
    const parsed = AccountSchema.safeParse(d.data())
    if (parsed.success) out.push(parsed.data)
  }
  return out.sort((a, b) => a.sortOrder - b.sortOrder)
}

export async function updateTransactionCategory(txId: string, categoryId: string | null): Promise<void> {
  const uid = requireUid()
  const ref = doc(firestore, 'users', uid, 'transactions', txId)
  // setDoc with merge so we don't clobber fields we haven't fetched
  // (e.g. Plaid `description`, `originalCategory`).
  await setDoc(ref, { categoryId }, { merge: true })
}

export async function updateTransactionCleared(
  txId: string,
  cleared: TransactionStatus,
): Promise<void> {
  const uid = requireUid()
  await setDoc(doc(firestore, 'users', uid, 'transactions', txId), { cleared }, { merge: true })
}

export interface SplitInput {
  categoryId: string
  amount: number // signed; outflows negative
  memo?: string | null
}

/**
 * Save splits for a transaction. Validates sum-equality with the
 * parent's amount before writing so callers can't ship inconsistent
 * data even by skipping the UI guard.
 *
 * Implementation note: SplitTransaction.transactionId === txId for
 * every split; iOS reads this back. We generate split ids
 * client-side so a previously-saved split can be edited without
 * forking. Passing `[]` clears all splits (reverts to a single-
 * category transaction).
 */
export async function updateTransactionSplits(
  tx: Transaction,
  splits: SplitInput[],
): Promise<void> {
  const uid = requireUid()
  if (splits.length > 0) {
    const sum = splits.reduce((s, p) => s + p.amount, 0)
    // Allow a 1¢ rounding tolerance — IEEE-754 surprises bite even
    // when the UI shows clean values.
    if (Math.abs(sum - tx.amount) > 0.01) {
      throw new Error(
        `Splits sum (${sum.toFixed(2)}) doesn't match transaction amount (${tx.amount.toFixed(2)})`,
      )
    }
  }
  const persisted = splits.map((s) => ({
    id: crypto.randomUUID(),
    transactionId: tx.id,
    categoryId: s.categoryId,
    amount: s.amount,
    memo: s.memo ?? null,
  }))
  await setDoc(
    doc(firestore, 'users', uid, 'transactions', tx.id),
    { splits: persisted, categoryId: null },
    { merge: true },
  )
  // The parent's categoryId becomes null when splits exist —
  // matches iOS expectations: splits own the categorization.
}

/**
 * Bulk recategorize. Issues N parallel merge-writes — Firestore
 * doesn't bill per request the way a backend round-trip would, and
 * keeping each write independent avoids the 500-op batched-write
 * cap iOS already accounts for elsewhere.
 *
 * Promise.allSettled vs Promise.all: we want the partial-success
 * story so a single broken doc doesn't roll back the rest. The
 * caller decides what to do with rejections.
 */
export async function bulkUpdateTransactionCategory(
  ids: string[],
  categoryId: string | null,
): Promise<{ ok: string[]; failed: Array<{ id: string; reason: unknown }> }> {
  const uid = requireUid()
  const results = await Promise.allSettled(
    ids.map((id) =>
      setDoc(doc(firestore, 'users', uid, 'transactions', id), { categoryId }, { merge: true }),
    ),
  )
  const ok: string[] = []
  const failed: Array<{ id: string; reason: unknown }> = []
  results.forEach((r, i) => {
    if (r.status === 'fulfilled') ok.push(ids[i])
    else failed.push({ id: ids[i], reason: r.reason })
  })
  return { ok, failed }
}

export interface NewTransactionInput {
  dateISO: string
  accountId: string
  amount: number // negative for outflow, positive for inflow
  payeeId?: string | null
  categoryId?: string | null
  memo?: string | null
  cleared?: TransactionStatus
}

/**
 * Creates a manual transaction. ID generated client-side so the
 * cache can optimistically include it without a server round-trip.
 * Matches the iOS Transaction shape exactly so the doc round-trips
 * cleanly to the iOS Codable reader.
 */
export async function createTransaction(input: NewTransactionInput): Promise<Transaction> {
  const uid = requireUid()
  const tx: Transaction = {
    id: crypto.randomUUID(),
    date: input.dateISO,
    payeeId: input.payeeId ?? null,
    categoryId: input.categoryId ?? null,
    accountId: input.accountId,
    amount: input.amount,
    memo: input.memo ?? null,
    cleared: input.cleared ?? 'uncleared',
    isInflow: input.amount > 0,
    splits: [],
    isTransfer: false,
    transferAccountId: null,
    transferId: null,
    isCreditCardPayment: false,
    originalCategory: null,
    description: null,
  }
  const ref = doc(firestore, 'users', uid, 'transactions', tx.id)
  await setDoc(ref, tx)
  return tx
}

